import { LiveChatWidget } from '@livechat/widget-react';
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
// @mui
import { Badge, Box, Grid, styled, Stack, Typography } from '@mui/material';
import ReactPixel from 'react-facebook-pixel';

// hooks
import { useSnackbar } from 'notistack';
import AiwinImageBanner from '../../assets/aiwin_casino.jpg';
import HistoryBanner from '../../assets/history/historyBanner.jpg';
import useSettings from '../../hooks/useSettings';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';

import AccountInfo from '../../views/user/AccountInfo';

import DashboardHeader from './header';

import { getGameCategsAndCompanies, getSiteSettings, getUserMemberBalance } from '../../redux/slices/lookup';

import useLocales from '../../hooks/useLocales';

import { setGameCategories, setAllGames, setActiveTab } from '../../redux/slices/game';

import conObj from '../../utils/connection-assistant';

import { usePageVisibility } from '../../hooks/usePageVisibility';
import {
  closeLoadingScreen,
  openLoadingScreen,
  setShowLiveChat,
  setShowDownloadApp,
} from '../../redux/slices/settings';

import Image from '../../components/Image';
import { BannerHeader } from '../../components/banner-header/BannerHeader';
import { Announcement } from '../../components/announcement/Announcement';
import useResponsive from '../../hooks/useResponsive';

import Home from '../../assets/navigation/home-classic.png';
import HomeHover from '../../assets/navigation/home-hover.png';
import Settings from '../../assets/navigation/settings.png';
import SettingsHover from '../../assets/navigation/settings-hover.png';
import Clock from '../../assets/navigation/clock.png';
import ClockHover from '../../assets/navigation/clock-hover.png';
import Gift from '../../assets/navigation/gift-classic.png';
import GiftHover from '../../assets/navigation/gift-hover.png';
import Deposit from '../../assets/navigation/deposit.png';
import DepositHover from '../../assets/navigation/deposit-hover.png';
import { fNumberDecimal } from '../../utils/formatNumber';
import PopupBanner from './PopupBanner';
import BannerCarousel from '../../views/game/BannerCarousel';
import WithdrawFormDialog from '../../views/withdraw/WithdrawFormDialog';
import NavigationHeader from '../../components/banner-header/NavigationHeader';
import Footer from './Footer';
import Iconify from '../../components/Iconify';
import MobileMenu from '../../components/menu-drawer/MobileMenuDrawer';
import AccountDrawer from './navbar/AccountDrawer';
import FloatingIcons from '../../components/FloatingIcons';
import { openAddMemberBankDialog } from '../../redux/slices/addMemberBankDialog';

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ showDownloadApp, isDesktop, collapseClick, theme }) => ({
  [theme.breakpoints.only('xs')]: {
    paddingTop: showDownloadApp && !isDesktop ? 36 : 0,
  },
  // flexGrow: 1,
  // height: '100%',
  // backgroundColor: 'black',
  // minHeight: '100vh',
  // paddingBottom: HEADER.MOBILE_HEIGHT,
  // [theme.breakpoints.up('lg')]: {
  //   transition: theme.transitions.create('margin-left', {
  //     duration: theme.transitions.duration.shorter,
  //   }),
  // },
}));

// ----------------------------------------------------------------------

const DashboardLayout = () => {
  const isDesktop = useResponsive('up', 'lg');

  const location = useLocation();

  const dispatchUi = useDispatch();

  const isActive = (path) => location.pathname.startsWith(path);

  const { isAuthenticated, userProfile, logout } = useAuth();

  const { siteSettings, showLiveChat, memberBalance, showDownloadApp } = useSelector((x) => ({
    ...x.lookup,
    ...x.settings,
  }));

  const isVisible = usePageVisibility();

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const isArrangement = ['arrangement'].some((path) => location.pathname.includes(path));

  const isHome = location.pathname === '/';

  const hasNoGame = ['promotion', 'referral', 'user', 'vip', 'history'].some((path) =>
    location.pathname.includes(path)
  );

  const [isFixed, setIsFixed] = useState(false);

  const { themeLayout } = useSettings();

  const [menuIndex, setMenuIndex] = useState(1);

  const intervalRef = useRef(null);

  const verticalLayout = themeLayout === 'vertical';

  const { enqueueSnackbar } = useSnackbar();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [openAccDrawer, setOpenAccDrawer] = useState(false);

  const [width, setWidth] = useState(window.innerWidth);

  const [height, setHeight] = useState(window.innerHeight);

  const updateDimensions = () => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const triggerPoint = 120;

      if (window.scrollY > triggerPoint) {
        setIsFixed(true);
      } else {
        setIsFixed(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // const promises = [getAllGames(), getGameCategsAndCompanies()];

    if (localStorage.getItem('referralCode')) {
      navigate('/');
      // dispatchUi(openRegister());
    }

    // getSiteSettings();

    // dispatch(openLoadingScreen());
    // Promise.all(promises).finally(() => dispatch(closeLoadingScreen()));
  }, []);

  /**
   * Call getUserMemberBalance() when tab is active and isAuthenticated
   * to keep the member balance updated
   */

  useEffect(() => {
    if (isVisible && isAuthenticated) {
      intervalRef.current = setInterval(() => {
        getUserMemberBalance().then((response) => {
          if (response?.data?.data?.data?.length === 0 && userProfile?.is_kyc_verified) {
            dispatch(openAddMemberBankDialog({ open: true }));
          }
        });
      }, 30000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [isVisible, isAuthenticated, userProfile?.is_kyc_verified]);

  useEffect(() => {
    try {
      if (siteSettings?.config_analytics) {
        const _ga = siteSettings?.config_analytics?.split(',');

        // Multiple products (previously known as trackers)
        ReactGA.initialize(
          _ga.map((item) => ({
            // measurement ID
            trackingId: item,
            // gaOptions: {...}, // optional
            // gtagOptions: {...}, // optional
          }))
          //   [
          //   {
          //     // measurement ID
          //     trackingId: _ga[0],
          //     // gaOptions: {...}, // optional
          //     // gtagOptions: {...}, // optional
          //   },
          // ]
        );
      }

      if (siteSettings?.config_fb_pixel) {
        const metaPixel = siteSettings.config_fb_pixel.replace(/[^\d,]+/g, '').split(',');
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: false, // enable logs
        };

        metaPixel.forEach((_pixel) => {
          ReactPixel.init(_pixel, options);

          ReactPixel.pageView(); // For tracking page view
        });
      }

      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const existingScript = document.getElementById(respondIo?.id);

      //   if (!existingScript) {
      //     const script = document.createElement('script');
      //     script.id = respondIo?.id;
      //     script.src = respondIo?.src;
      //     script.async = true;

      //     document.head.appendChild(script);
      //   }
      // }

      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const existingScript = document.getElementById(respondIo?.id);

      //   if (!existingScript) {
      //     const script = document.createElement('script');
      //     script.id = respondIo?.id;
      //     script.src = respondIo?.src;
      //     script.async = true;

      //     document.head.appendChild(script);
      //   }
      // }
    } catch (error) {
      console.warn(`3RD PARTY INTEGRATION ERROR: ${error?.message}`);
    }
  }, [siteSettings]);

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);

    // Check if the PWA is already installed (usually stored in localStorage or can be checked with appinstalled event)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      dispatch(setShowDownloadApp(false));
    }

    // Listen for the `appinstalled` event
    window.addEventListener('appinstalled', () => {
      dispatch(setShowDownloadApp(false));
    });

    return () => {
      window.removeEventListener('resize', updateDimensions);

      window.removeEventListener('appinstalled', () => {
        dispatch(setShowDownloadApp(false));
      });
    };
  }, []);

  const getAllGames = () => {
    conObj.post('games/all.json?limit=1').then((res) => {
      if (res.status === 200) {
        // res.data?.data?.data
        // set appversion to localstorage
        // res.data?.data.appversion
        const _allGames = res.data?.data?.data;
        dispatch(setAllGames(_allGames));
        dispatch(
          setGameCategories(
            _allGames
              .filter((f) => f.key.toLowerCase().indexOf('gcat') !== -1)
              .map((item) => ({ key: item.key, name: item.name, slug: item.slug }))
          )
        );
      }
    });
  };

  const [openSettings, setOpenSettings] = useState(false);

  const handleOpen = () => {
    setOpenSettings(!openSettings);
  };

  const handleClose = () => {
    setOpenSettings(false);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  if (verticalLayout) {
    return (
      <>
        <Box
          component="main"
          sx={{
            backgroundColor: 'black',
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  const navItems = [
    {
      label: 'menu',
      icon: 'mdi:microsoft-xbox-controller-menu',
      onClick: () => setOpenDrawer(true),
    },
    {
      label: 'referral',
      icon: 'mingcute:group-3-fill',
      onClick: () => navigate('/referral'),
    },
    {
      label: 'deposit',
      icon: 'fa6-solid:circle-dollar-to-slot',
      onClick: () => navigate('/user/wallet?tab=deposit'),
    },
    {
      label: 'promo_center',
      icon: 'ion:gift',
      onClick: () => navigate('/promotion/tab'),
    },
    {
      label: 'account',
      icon: 'ion:person',
      onClick: () => setOpenAccDrawer(true),
    },
  ];

  return (
    <>
      <MainStyle
        showDownloadApp={showDownloadApp}
        isDesktop={isDesktop}
        sx={{
          position: 'relative',
        }}
      >
        {isDesktop && (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1100,
                backgroundColor: 'white',
                boxShadow: 1,
              }}
            >
              <DashboardHeader setOpenAccDrawer={setOpenAccDrawer} openAccDrawer={openAccDrawer} />
              <NavigationHeader />
            </Box>
            <Outlet />
          </>
        )}

        {!isDesktop && (
          <>
            <Box
              sx={{
                position: isFixed ? 'fixed' : 'sticky',
                top: showDownloadApp ? 36 : 0,
                width: '100%',
                zIndex: 1100,
                backgroundColor: 'white',
                boxShadow: 1,
              }}
            >
              <DashboardHeader
                setOpenAccDrawer={setOpenAccDrawer}
                openAccDrawer={openAccDrawer}
                showDownloadApp={showDownloadApp}
                // setShowDownloadApp={setShowDownloadApp}
              />
            </Box>
            <Outlet />
          </>
        )}

        <PopupBanner />
        {!isArrangement && <Footer />}

        {/* Bottom Navigation */}
        {!isDesktop && (
          <Grid
            container
            sx={{
              position: 'fixed',
              height: '50px',
              bottom: 0,
              left: 0,
              width: '100%',
              bgcolor: '#2d4b3a',
              display: 'flex',
              justifyContent: 'space-around',
              // py: 1.2,
              background: 'linear-gradient(180deg, #3b5f47 0%, #1f3528 100%)',
              boxShadow: '0 -2px 12px rgba(0,0,0,0.4)',
            }}
          >
            {navItems.map((item, idx) => (
              <Grid item xs={12 / 5} sx={{ display: 'flex' }} key={idx}>
                <Stack
                  key={item.label}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    m: 'auto',
                    color: '#f5c16c',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => {
                    item.onClick();
                    if (openAccDrawer) setOpenAccDrawer(false);
                  }}
                >
                  <Badge
                    color="error"
                    variant="dot"
                    overlap="circular"
                    invisible={!(item.label === 'account' && isAuthenticated && !userProfile?.is_kyc_verified)}
                  >
                    <Iconify icon={item.icon} width={25} height={25} />
                  </Badge>
                  <Typography variant="caption" sx={{ fontSize: '10px', color: '#adacb1', fontWeight: 700 }}>
                    {translate(item.label)}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        )}

        <MobileMenu open={openDrawer} onClose={() => setOpenDrawer(false)} />
        {/* {isArrangement && (
          <Image
            src={AiwinImageBanner}
            sx={{
              width: '100%',
              aspectRatio: { xs: '20/7', md: '10/2' },
              objectFit: 'cover',
            }}
          />
        )}

        {isHistory && (
          <Image
            src={HistoryBanner}
            sx={{
              width: '100%',
              aspectRatio: { xs: '12/5', md: '10/2' },
              objectFit: 'cover',
            }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            backgroundColor: '#232734',
            alignContent: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              maxWidth: '1250px',
              width: '100%',
            }}
          >
            <Outlet />
          </Box>
        </Box> */}

        {/* {!isMobile && <MyFooter />} */}

        {/* {!isDesktop && (
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue);
            navigate(bottomNavigation[newValue].path);
          }}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            paddingX: '4px',
            height: '75px',
            backgroundColor: '#1a212eff',
          }}
        >
          {bottomNavigation.map((items, index) => (
            <BottomNavigationAction
              disableRipple
              key={index}
              label={capitalize(items.title)}
              icon={<IconComponent icon={value === index ? items.active : items.icon} />}
              sx={{
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '8.47px',
                  color: value === index ? '#F49825' : 'white',
                  mt: '4px',
                },
                '& .MuiBottomNavigationAction-root.Mui-selected': {
                  transform: 'none',
                },
              }}
            />
          ))}
        </BottomNavigation>
      )} */}
        {/* {!isDesktop && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              paddingX: '4px',
              height: '75px',
              zIndex: 100,
              backgroundColor: '#1a212eff',
            }}
          >
            <Box display="flex" justifyContent="space-between" py={1} px={2}>
              {bottomNavItems.map((item, index) => {
                const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);

                return (
                  <Box
                    key={index}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={0.5}
                    sx={{ cursor: 'pointer', width: 'fit-content' }}
                    onClick={item.onClick}
                  >
                    {active ? (
                      <Image src={item.activeIcon} sx={{ width: '32px', height: '32px' }} />
                    ) : (
                      <Image src={item.icon} sx={{ width: '32px', height: '32px' }} />
                    )}

                    <Typography color={active ? '#ffcc00' : 'white'} fontSize="10px">
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )} */}

        {siteSettings?.config_livechat && siteSettings?.config_livechat?.length === 8 ? (
          <LiveChatWidget
            license={siteSettings?.config_livechat}
            visibility={showLiveChat ? 'maximized' : 'minimized'}
            onReady={(data) => {
              const widgetContainer = document.getElementById('chat-widget-container');

              if (widgetContainer && data.state.visibility === 'minimized') {
                widgetContainer.classList.add('adjust-bottom-space');
              }
            }}
            onVisibilityChanged={(data) => {
              const widgetContainer = document.getElementById('chat-widget-container');

              if (widgetContainer) {
                if (data.visibility === 'maximized') {
                  dispatch(setShowLiveChat(true));
                  widgetContainer.classList.remove('adjust-bottom-space');
                } else {
                  widgetContainer.classList.add('adjust-bottom-space');
                  dispatch(setShowLiveChat(false));
                }
              }
            }}
          />
        ) : (
          <></>
        )}

        {isAuthenticated && (
          <>
            <AccountInfo open={drawerOpen} handleClose={toggleDrawer(false)} />
            <WithdrawFormDialog />
          </>
        )}

        <AccountDrawer open={openAccDrawer} onClose={() => setOpenAccDrawer(false)} />

        {/* {isAuthenticated && (
          <Box>
            <SwipeableDrawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
              <Box width={250} role="presentation">
                <List>
                  <ListItemButton>
                    <ListItemText primary={userProfile?.full_name} />
                  </ListItemButton>
                </List>

                <Divider />

                <List>
                  <ListItemButton>
                    <ListItemText
                      primary={`${memberBalance?.currencyCode} ${
                        memberBalance?.displayBalance ? fNumberDecimal(memberBalance?.displayBalance) : 0
                      }`}
                    />
                  </ListItemButton>
                </List>

                <Divider />

                <List>
                  <ListItemButton
                    onClick={() => {
                      navigate('/user/account');
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={translate('account')} />
                  </ListItemButton>

                  <ListItemButton
                    onClick={() => {
                      navigate('/user/banks');
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={translate('bank_account')} />
                  </ListItemButton>

                  <ListItemButton
                    onClick={() => {
                      navigate('/referral');
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={translate('referral')} />
                  </ListItemButton>

                  <ListItemButton
                    onClick={() => {
                      navigate('/history/tab');
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={translate('history')} />
                  </ListItemButton>

                  <ListItemButton
                    onClick={() => {
                      handleLogout();
                      navigate('/');
                      setDrawerOpen(false);
                    }}
                  >
                    <ListItemText primary={translate('logout')} />
                  </ListItemButton>
                </List>
              </Box>
            </SwipeableDrawer>
          </Box>
        )} */}
      </MainStyle>
    </>
  );
};

export default DashboardLayout;
